import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MATERIALS, ROOM_RECOMMENDATIONS } from "@/lib/materialsData";
import { BarChart3, Star, ArrowUpDown } from "lucide-react";

const STAR_DISPLAY = (count) => {
  return Array(5).fill(0).map((_, i) => (
    <Star key={i} className={`h-3.5 w-3.5 inline ${i < count ? "fill-[#FFA500] text-[#FFA500]" : "text-[#E2E8F0]"}`} />
  ));
};

export default function MaterialComparator({ roomType, rooms, onSelect, open, onOpenChange }) {
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const allRoomTypes = rooms && rooms.length > 0
    ? [...new Set(rooms.map((r) => r.type || r.name).filter(Boolean))]
    : roomType ? [roomType] : [];

  const recommended = allRoomTypes.length > 0
    ? [...new Set(allRoomTypes.flatMap((rt) => ROOM_RECOMMENDATIONS[rt] || []))]
    : [];

  const materialsArray = Object.entries(MATERIALS).map(([id, mat]) => ({
    id,
    name: mat.name,
    durability: mat.comparator.durability,
    waterResistance: mat.comparator.waterResistance,
    maintenance: mat.comparator.maintenance,
    sustainability: mat.comparator.sustainability,
    priceMin: mat.priceRange[0],
    priceMax: mat.priceRange[1],
    isRecommended: recommended.includes(id),
  }));

  const sorted = [...materialsArray].sort((a, b) => {
    if (!sortBy) {
      if (a.isRecommended && !b.isRecommended) return -1;
      if (!a.isRecommended && b.isRecommended) return 1;
      return 0;
    }
    let aVal, bVal;
    if (sortBy === "price") { aVal = a.priceMin; bVal = b.priceMin; }
    else if (sortBy === "durability") { aVal = a.durability; bVal = b.durability; }
    else if (sortBy === "water") { aVal = a.waterResistance; bVal = b.waterResistance; }
    else { aVal = a.name; bVal = b.name; }
    if (sortDir === "asc") return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  const handleSort = (col) => {
    if (sortBy === col) { setSortDir(d => d === "asc" ? "desc" : "asc"); }
    else { setSortBy(col); setSortDir("asc"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="material-comparator-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#1B2B4D] flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[#0066FF]" />
            Comparador de Materiales
          </DialogTitle>
          {allRoomTypes.length > 0 && (
            <p className="text-sm text-[#4A5568] mt-1">
              {allRoomTypes.length === 1 ? "Estancia detectada:" : "Estancias detectadas:"}{" "}
              {allRoomTypes.map((rt, i) => (
                <Badge key={i} className="bg-[#FFF3E0] text-[#E65100] border-[#FFA500]/20 ml-1">{rt}</Badge>
              ))}
            </p>
          )}
        </DialogHeader>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-heading text-[#1B2B4D]">Material</TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("durability")} data-testid="sort-durability">
                  <span className="flex items-center gap-1">Durabilidad <ArrowUpDown className="h-3 w-3" /></span>
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("water")} data-testid="sort-water">
                  <span className="flex items-center gap-1">Resist. Agua <ArrowUpDown className="h-3 w-3" /></span>
                </TableHead>
                <TableHead>Mantenimiento</TableHead>
                <TableHead>Sostenibilidad</TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("price")} data-testid="sort-price">
                  <span className="flex items-center gap-1">Precio EUR/m² <ArrowUpDown className="h-3 w-3" /></span>
                </TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((mat) => (
                <TableRow
                  key={mat.id}
                  className={mat.isRecommended ? "bg-[#E3F2FD]/30" : ""}
                  data-testid={`comparator-row-${mat.id}`}
                >
                  <TableCell className="font-medium text-[#1B2B4D]">
                    <div className="flex items-center gap-2">
                      {mat.name}
                      {mat.isRecommended && (
                        <Badge className="bg-[#E3F2FD] text-[#0066FF] border-[#0066FF]/20 text-[10px]">
                          Recomendado
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{STAR_DISPLAY(mat.durability)}</TableCell>
                  <TableCell>{STAR_DISPLAY(mat.waterResistance)}</TableCell>
                  <TableCell className="text-sm text-[#4A5568]">{mat.maintenance}</TableCell>
                  <TableCell className="text-sm text-[#4A5568]">{mat.sustainability}</TableCell>
                  <TableCell className="text-sm font-medium text-[#1B2B4D]">{mat.priceMin}-{mat.priceMax}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      className="bg-[#0066FF] hover:bg-[#0052CC] text-white text-xs"
                      onClick={() => { onSelect(mat.id); onOpenChange(false); }}
                      data-testid={`select-material-${mat.id}`}
                    >
                      Seleccionar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
